import type { XInput } from '@playcastdotio/CoreTypes';
import clsx from 'clsx';
// import { useMobilePortrait } from './useMobilePortrait';

interface GamepadStateProps {
  gamepad: XInput;
  location: 'left' | 'right';
  value: number;
  customIcon?: string; // URL to custom thumbstick icon
  customSize?: number; // Size in pixels for custom icon
  useCircularStyle?: boolean; // Use circular button style (static outer ring, movable inner circle)
}

export const Thumbstick = (props: GamepadStateProps) => {
  const { gamepad, location, customIcon, customSize = 127, useCircularStyle = false } = props;
  // const isMobilePortrait = useMobilePortrait();

  // XInput thumbstick values range from -32768 to 32767
  const x = location === 'left' ? gamepad.sThumbLX : gamepad.sThumbRX;
  const y = location === 'left' ? gamepad.sThumbLY : gamepad.sThumbRY;

  const baseX = 1;
  const baseY = 1;

  const maxDelta = 20;

  const cx = baseX + (x / 32767) * maxDelta * 1;
  const cy = baseY + (y / 32767) * maxDelta * -1;

  const isThumbstickMoving = Math.abs(x) > 1000 || Math.abs(y) > 1000;
  const fillColor = isThumbstickMoving ? `rgba(250, 204, 21, 255)` : 'black';

  // Circular button style: static outer ring with movable inner circle
  if (useCircularStyle) {
    const size = customSize;
    const outerSize = size;
    const innerSize = size * 0.4; // Inner circle is 40% of outer size
    const maxMovement = (outerSize - innerSize) / 2 - 4; // Max distance inner can move (minus padding)

    // Calculate inner circle position based on thumbstick input
    const innerX = (x / 32767) * maxMovement;
    const innerY = (y / 32767) * maxMovement * -1; // Invert Y for screen coordinates

    return (
      <svg
        id={(location as string).concat('-Stick')}
        width={outerSize}
        height={outerSize}
        viewBox={`0 0 ${outerSize} ${outerSize}`}
        xmlns='http://www.w3.org/2000/svg'
        className={clsx('pointer-events-auto touch-none')}
        style={{ overflow: 'visible', willChange: 'filter' }}
      >
        {/* Outer circle - static with gray background */}
        {customIcon ? (
          <>
            {/* Gray background circle behind the icon */}
            <circle
              cx={outerSize / 2}
              cy={outerSize / 2}
              r={outerSize / 2 - 4}
              fill='rgba(100, 100, 100, 0.5)'
              className='pointer-events-none'
            />
            <image
              href={customIcon}
              x='0'
              y='0'
              width={outerSize}
              height={outerSize}
              opacity='0.75'
              className='pointer-events-none'
            />
          </>
        ) : (
          <svg x='0' y='0' width={outerSize} height={outerSize} viewBox='0 0 358 358' className='pointer-events-none' style={{ overflow: 'visible' }}>
            <defs>
              <filter id={`${location}-outer-filter`} x='0' y='0' width='358' height='358' filterUnits='userSpaceOnUse' colorInterpolationFilters='sRGB'>
                <feFlood floodOpacity='0' result='BackgroundImageFix'/>
                <feColorMatrix in='SourceAlpha' type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0' result='hardAlpha'/>
                <feOffset dx='2' dy='3'/>
                <feGaussianBlur stdDeviation='2'/>
                <feComposite in2='hardAlpha' operator='out'/>
                <feColorMatrix type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.7 0'/>
                <feBlend mode='normal' in2='BackgroundImageFix' result='effect1_dropShadow'/>
                <feBlend mode='normal' in='SourceGraphic' in2='effect1_dropShadow' result='shape'/>
              </filter>
              <linearGradient id={`${location}-outer-gradient`} x1='352' y1='309.177' x2='1.99999' y2='42.8234' gradientUnits='userSpaceOnUse'>
                <stop offset='0.25' stopColor='#1E1E1E'/>
                <stop offset='0.490385' stopColor='#505050'/>
                <stop offset='1' stopColor='#848484'/>
              </linearGradient>
            </defs>
            <g filter={`url(#${location}-outer-filter)`}>
              <circle cx='177' cy='176' r='175' fill='#666666' fillOpacity='0.1' shapeRendering='crispEdges'/>
              <circle cx='177' cy='176' r='173.5' stroke={`url(#${location}-outer-gradient)`} strokeWidth='3' fill='none' shapeRendering='crispEdges'/>
            </g>
          </svg>
        )}

        {/* Inner circle - movable */}
        <svg
          x={outerSize / 2 - innerSize / 2 + innerX}
          y={outerSize / 2 - innerSize / 2 + innerY}
          width={innerSize}
          height={innerSize}
          viewBox='0 0 190 190'
          className='pointer-events-none'
          style={{
            overflow: 'visible',
            ...(isThumbstickMoving ? { filter: 'drop-shadow(0 0 8px rgba(250,204,21,0.75))' } : {}),
          }}
        >
          <defs>
            <filter id={`${location}-inner-filter`} x='0' y='0' width='189.806' height='189.806' filterUnits='userSpaceOnUse' colorInterpolationFilters='sRGB'>
              <feFlood floodOpacity='0' result='BackgroundImageFix'/>
              <feColorMatrix in='SourceAlpha' type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0' result='hardAlpha'/>
              <feOffset dx='2' dy='2'/>
              <feGaussianBlur stdDeviation='0.9'/>
              <feComposite in2='hardAlpha' operator='out'/>
              <feColorMatrix type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0'/>
              <feBlend mode='normal' in2='BackgroundImageFix' result='effect1_dropShadow'/>
              <feBlend mode='normal' in='SourceGraphic' in2='effect1_dropShadow' result='shape'/>
            </filter>
            <linearGradient id={`${location}-inner-gradient`} x1='184.507' y1='170.946' x2='1.5' y2='15.0603' gradientUnits='userSpaceOnUse'>
              <stop offset='0.25' stopColor='#1E1E1E' stopOpacity='0.75'/>
              <stop offset='0.502358' stopColor='#666666'/>
              <stop offset='1' stopColor='white' stopOpacity='0.25'/>
            </linearGradient>
          </defs>
          <g filter={`url(#${location}-inner-filter)`}>
            <circle cx='93.0033' cy='93.0033' r='91.5033' fill={isThumbstickMoving ? 'rgba(250, 204, 21, 1)' : 'white'} fillOpacity={isThumbstickMoving ? 1 : 0.1} shapeRendering='crispEdges'/>
            <circle cx='93.0033' cy='93.0033' r='91.5033' stroke='#444444' strokeWidth='3' fill='none' shapeRendering='crispEdges'/>
            <circle cx='93.0033' cy='93.0033' r='91.5033' stroke={`url(#${location}-inner-gradient)`} strokeWidth='3' fill='none' shapeRendering='crispEdges'/>
          </g>
        </svg>
      </svg>
    );
  }

  // If custom icon is provided, render an image instead of SVG (legacy style - entire stick moves)
  if (customIcon) {
    return (
      <div
        id={(location as string).concat('-Stick')}
        className={clsx(
          'pointer-events-auto touch-none overflow-visible flex items-center justify-center',
        )}
        style={{
          transform: `translate(${cx}px, ${cy}px)`,
          filter: isThumbstickMoving ? 'drop-shadow(0 0 8px rgba(250,204,21,0.75))' : 'none',
          willChange: 'filter',
          width: `${customSize}px`,
          height: `${customSize}px`,
        }}
      >
        <img
          src={customIcon}
          alt={`${location} thumbstick`}
          className='pointer-events-none select-none w-full h-full object-contain'
          draggable={false}
          style={{
            opacity: isThumbstickMoving ? 1 : 0.75,
          }}
        />
      </div>
    );
  }

  // Default SVG thumbstick
  return (
    <svg
      id={(location as string).concat('-Stick')}
      width='127'
      height='127'
      viewBox='0 0 127 127'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={clsx(
        // isThumbstickMoving ? 'drop-shadow-[0_0_10px_rgba(250,204,21,0.75)]' : '',
        'pointer-events-auto touch-none overflow-visible',
      )}
      style={{
        transform: `translate(${cx}px, ${cy}px)`,
        filter: isThumbstickMoving ? 'drop-shadow(0 0 8px rgba(250,204,21,0.75))' : 'none',
        willChange: 'filter',
      }}
    >
      <g style={{ filter: 'drop-shadow(0 5.75px 2.875px rgba(0,0,0,0.25))' }}>
        <rect className='pointer-events-none' x='6' width='115' height='115' rx='57.5' fill={fillColor} fillOpacity='0.75' />
        <rect
          className='pointer-events-none'
          x='7.4375'
          y='1.4375'
          width='112.125'
          height='112.125'
          rx='56.0625'
          stroke='white'
          strokeOpacity='0.75'
          strokeWidth='2.875'
        />
        <circle className='pointer-events-none' cx='63.5' cy='23' r='5.75' fill='white' fillOpacity='0.75' />
        <circle className='pointer-events-none' cx='63.5' cy='92' r='5.75' fill='white' fillOpacity='0.75' />
        <circle className='pointer-events-none' cx='98' cy='57.5' r='5.75' transform='rotate(90 98 57.5)' fill='white' fillOpacity='0.75' />
        <circle className='pointer-events-none' cx='29' cy='57.5' r='5.75' transform='rotate(90 29 57.5)' fill='white' fillOpacity='0.75' />
      </g>
    </svg>
  );
};
